import React from 'react'
import { makeStyles, IconButton, TextField, MenuItem, Select, Button } from '@material-ui/core'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import moment from 'moment'

import {
  getEventsFromSchedule,
  WEEKDAYS,
  WEEK_INTERVALS,
  INTERVALS,
} from './index'

const useScheduleStyles = makeStyles((theme) => ({
  removeScheduleButton: {
    position: 'absolute',
    right: '0',
    top: '0',
    padding: '10px',
  },
  intervalSelect: {
    marginLeft: '1em',
    '& > div': {
      padding: '1em 2em',
    },
  },
  scheduleContainer: {
    position: 'relative',
    padding: '1em',
    border: '1px solid #c8c3c3',
    borderRadius: '5px',
    margin: '1em 0',
    backgroundColor: 'white',
  },
  scheduleSection: {
    display: 'flex',
    alignItems: 'center',
  },
  scheduleSectionContainer: {
    display: 'flex',
    width: '8ch',
  },
  scheduleSectionName: {
    color: 'gray',
  },
  scheduleSectionDivider: {
    flexGrow: 1,
    borderBottom: '2px solid #dfdfdf',
    alignSelf: 'center',
    marginLeft: '5px',
    marginRight: '3px',
  },
  selectedDay: {
    '&:hover': {
      filter: 'brightness(85%)',
      backgroundColor: '#439dcf',
    },
    transition: 'filter 0.25s ease',
    backgroundColor: '#439dcf',
    color: 'white',
    minWidth: 0,
    height: '2.5em',
    margin: '0.5em',
    marginLeft: 0,
    border: '1px solid #d7d2d2',
    fontSize: '16px',
  },
  unselectedDay: {
    '&:hover': {
      filter: 'brightness(85%)',
      backgroundColor: 'white',
    },
    transition: 'filter 0.25s ease',
    backgroundColor: 'white',
    color: '#555d65',
    minWidth: 0,
    height: '2.5em',
    margin: '0.5em',
    marginLeft: 0,
    border: '1px solid #d7d2d2',
    fontSize: '16px',
  },
}))

const Schedule = (props) => {
  const { edits, schedule, onChange, onRemove } = props
  const classes = useScheduleStyles()
  const handleChangeEvery = (every) => {
    const newValue = Math.max(1, every) || 1
    onChange({ ...schedule, every: newValue })
  }
  const handleChangeInterval = (interval) => {
    onChange({ ...schedule, interval, every: 1 })
  }
  const handleChangeWeekInterval = (weekInterval) => {
    onChange({ ...schedule, weekInterval })
  }
  const handleChangeOnWeekday = (weekday) => {
    let newDays = [...schedule.on]
    // toggle whether this weekday is scheduled on
    if (schedule.on.some((day) => day.value === weekday.value)) {
      newDays = [...newDays].filter((day) => day.value !== weekday.value)
    } else {
      newDays = [...newDays, weekday]
    }
    onChange({ ...schedule, on: newDays })
  }
  const handleDatePickerChange = (ev) => {
    if (ev.isSameOrAfter(edits.startDateTime)) {
      onChange({ ...schedule, until: ev })
    } else {
      // tried to make a schedule that ends before the main event starts
      return
    }
  }
  const handleRemoveSchedule = () => {
    onRemove()
  }
  const scheduledEvents = getEventsFromSchedule(edits, schedule)
  const MAX_DATE = moment(new Date()).add(12, 'months').toDate()
  return (
    <div className={classes.scheduleContainer}>
      <IconButton
        color='secondary'
        onClick={handleRemoveSchedule}
        className={classes.removeScheduleButton}
      >
        <i className='fas fa-trash-alt' />
      </IconButton>
      <div className={classes.scheduleSection}>
        <div className={classes.scheduleSectionContainer}>
          <span className={classes.scheduleSectionName}>Every</span>
          <span className={classes.scheduleSectionDivider} />
        </div>
        {schedule.interval === INTERVALS.WEEK.key ? (
          <TextField
            style={{ maxWidth: '9ch' }}
            variant='outlined'
            size='small'
            value={schedule.every}
            onChange={(ev) => handleChangeEvery(ev.target.value)}
            type='number'
          />
        ) : null}
        <Select
          variant='outlined'
          size='small'
          value={schedule.interval}
          className={classes.intervalSelect}
          onChange={(ev) => handleChangeInterval(ev.target.value)}
        >
          {Object.values(INTERVALS).map((interval) => {
            return (
              <MenuItem
                key={`month select ${interval.key}`}
                value={interval.key}
              >
                {schedule.every !== 0 ? interval.plural : interval.label}
              </MenuItem>
            )
          })}
        </Select>
      </div>
      <div className={classes.scheduleSection}>
        <div className={classes.scheduleSectionContainer}>
          <span className={classes.scheduleSectionName}>On</span>
          <span className={classes.scheduleSectionDivider} />
        </div>
        {schedule.interval === INTERVALS.MONTH.key ? (
          <Select
            value={schedule.weekInterval.key}
            style={{ marginRight: '0.5em' }}
            size='small'
            onChange={(ev) =>
              handleChangeWeekInterval(WEEK_INTERVALS[ev.target.value])
            }
          >
            {Object.values(WEEK_INTERVALS).map((interval) => {
              return <MenuItem key={`menu item ${interval}`} value={interval.key}>{interval.label}</MenuItem>
            })}
          </Select>
        ) : null}
        {Object.values(WEEKDAYS).map((weekday, i) => {
          return (
            <Button
              key={`weekday button ${i}`}
              onClick={() => handleChangeOnWeekday(weekday)}
              className={
                schedule.on.some((day) => day.value === weekday.value)
                  ? classes.selectedDay
                  : classes.unselectedDay
              }
            >
              {weekday.label.substring(0, 3).toUpperCase()}
            </Button>
          )
        })}
      </div>
      <div className={classes.scheduleSection}>
        <div className={classes.scheduleSectionContainer}>
          <span className={classes.scheduleSectionName}>Until</span>
          <span className={classes.scheduleSectionDivider} />
        </div>
        <div style={{ marginRight: '0.5em' }}>
          <ReactDatePicker
            fixedHeight={true}
            selected={schedule.until}
            peekNextMonth
            className='form-control'
            dateFormat='MM/DD/YYYY'
            onChange={handleDatePickerChange}
            maxDate={MAX_DATE}
            minDate={edits.startDateTime}
            highlightDates={scheduledEvents.map((event) => event.startDateTime)}
          />
        </div>
      </div>
    </div>
  )
}

export default Schedule
